import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Brain,
  CircleAlert,
  ExternalLink,
  FileUp,
  Inbox,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  ShieldCheck,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AgentModeToggle from '../components/agent/AgentModeToggle';
import ContextRail, { parseRailTab, type RailTab } from '../components/agent/ContextRail';
import MultiFileDropzone from '../components/agent/MultiFileDropzone';
import ChatPanel from '../components/chat/ChatPanel';
import ConversationSidebar from '../components/chat/ConversationSidebar';
import {
  answerAgentQuestion,
  deleteConversation,
  fetchAiStatus,
  fetchConversations,
  fetchSandboxStatus,
  updateConversation,
  type AgentTask,
  type AiStatus,
  type ConversationSummary,
  type SandboxStatus,
} from '../lib/api';

const TEMPLATES = [
  { value: '', label: 'General assistant' },
  { value: 'business_qa', label: 'Business Q&A' },
  { value: 'invoice_draft', label: 'Invoice drafting' },
  { value: 'contract_action', label: 'Contract actions' },
  { value: 'activity_summary', label: 'Activity summary' },
  { value: 'sync_failure_explanation', label: 'Sync failure help' },
];

const AGENT_MODE_KEY = 'zt.ai.agentMode';
const TEMPLATE_KEY = 'zt.ai.template';

function useIsNarrow(breakpoint = 1280) {
  const [narrow, setNarrow] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

function useIsMobile(breakpoint = 1024) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return mobile;
}

function StatusBar({
  status,
  sandbox,
}: {
  status: AiStatus | null;
  sandbox: SandboxStatus | null;
}) {
  if (!status) return null;

  const items: Array<{ label: string; tone: 'ok' | 'warn' | 'bad' }> = [
    {
      label: status.configured
        ? `${status.provider ?? 'provider'} · ${status.model ?? 'model'}`
        : 'AI not configured',
      tone: status.configured ? 'ok' : 'bad',
    },
    {
      label: status.mcpConnected ? `${status.toolCount} MCP tools` : 'MCP offline',
      tone: status.mcpConnected ? 'ok' : 'bad',
    },
    { label: `OCR: ${status.ocrProvider ?? 'none'}`, tone: status.ocrProvider ? 'ok' : 'warn' },
    {
      label: `${status.memoryFacts} memory facts`,
      tone: status.memoryFacts > 0 ? 'ok' : 'warn',
    },
  ];

  if (sandbox) {
    items.push({
      label: sandbox.enabled
        ? `Sandbox · ${sandbox.backend}`
        : 'Sandbox off',
      tone: sandbox.enabled ? 'ok' : 'warn',
    });
  }

  const toneClass = {
    ok: 'bg-zt-primary',
    warn: 'bg-zt-warning',
    bad: 'bg-zt-danger',
  } as const;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-zt-muted">
          <span className={`h-1.5 w-1.5 rounded-full ${toneClass[item.tone]}`} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function HubJump({
  to,
  icon: Icon,
  label,
  onClick,
}: {
  to?: string;
  icon: typeof ShieldCheck;
  label: string;
  onClick?: () => void;
}) {
  const className =
    'inline-flex items-center gap-1.5 rounded-md border border-zt-border px-2 py-1 text-[11px] text-zt-muted transition hover:border-zt-primary hover:text-zt-primary';

  if (to) {
    return (
      <Link to={to} className={className}>
        <Icon size={12} />
        {label}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      <Icon size={12} />
      {label}
    </button>
  );
}

function AiConsole() {
  const { conversationId: routeConversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const isNarrow = useIsNarrow();

  const [template, setTemplate] = useState(() => localStorage.getItem(TEMPLATE_KEY) ?? '');
  const [agentMode, setAgentMode] = useState(
    () => (localStorage.getItem(AGENT_MODE_KEY) ?? 'true') === 'true'
  );
  const [agentTasks, setAgentTasks] = useState<AgentTask[]>([]);
  const [clarifyTaskId, setClarifyTaskId] = useState<string | null>(null);

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [railOpen, setRailOpen] = useState(() => window.innerWidth >= 1280);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [sandbox, setSandbox] = useState<SandboxStatus | null>(null);
  const [railTab, setRailTab] = useState<RailTab>(
    () => parseRailTab(searchParams.get('rail')) ?? 'tasks'
  );

  const activeId = routeConversationId ?? null;

  useEffect(() => {
    localStorage.setItem(AGENT_MODE_KEY, String(agentMode));
  }, [agentMode]);

  useEffect(() => {
    localStorage.setItem(TEMPLATE_KEY, template);
  }, [template]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  useEffect(() => {
    setRailOpen(!isNarrow);
  }, [isNarrow]);

  // Deep-link: /ai?rail=memory|approvals|inbox|tools|tasks
  useEffect(() => {
    const fromQuery = parseRailTab(searchParams.get('rail'));
    if (fromQuery) {
      setRailTab(fromQuery);
      setRailOpen(true);
    }
  }, [searchParams]);

  const loadConversations = useCallback(async () => {
    setListLoading(true);
    const res = await fetchConversations({
      q: debouncedQuery || undefined,
      archived: showArchived,
      limit: 100,
    });
    setConversations(res.ok && res.data ? res.data.items : []);
    setListLoading(false);
  }, [debouncedQuery, showArchived]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    void fetchAiStatus().then((res) => setStatus(res.ok && res.data ? res.data : null));
    void fetchSandboxStatus().then((res) => setSandbox(res.ok && res.data ? res.data : null));
  }, []);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId]
  );

  // A conversation remembers the mode and template it was started with, so
  // switching between chats restores the right configuration. This syncs on
  // identity change only — refreshing the list must not undo a manual toggle.
  const syncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeId) {
      syncedRef.current = null;
      return;
    }
    if (!activeConversation || syncedRef.current === activeId) return;
    syncedRef.current = activeId;
    setAgentMode(activeConversation.agentMode);
    setTemplate(activeConversation.template ?? '');
  }, [activeConversation, activeId]);

  const handleAgentModeChange = useCallback(
    (value: boolean) => {
      setAgentMode(value);
      if (activeId) void updateConversation(activeId, { agentMode: value });
    },
    [activeId]
  );

  const handleTemplateChange = useCallback(
    (value: string) => {
      setTemplate(value);
      if (activeId) void updateConversation(activeId, { template: value });
    },
    [activeId]
  );

  const handleSelect = useCallback(
    (id: string) => {
      setAgentTasks([]);
      navigate(`/ai/${id}${searchParams.toString() ? `?${searchParams}` : ''}`);
      if (isMobile) setSidebarOpen(false);
    },
    [navigate, searchParams, isMobile]
  );

  // The conversation record is created by the first turn, so a new chat is just
  // an empty console — no placeholder rows pile up if the user never sends.
  const handleNew = useCallback(() => {
    setAgentTasks([]);
    navigate('/ai');
  }, [navigate]);

  const handleConversationId = useCallback(
    (id: string) => {
      if (id !== activeId) navigate(`/ai/${id}`, { replace: true });
    },
    [activeId, navigate]
  );

  const handleRename = useCallback(async (id: string, title: string) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
    await updateConversation(id, { title });
  }, []);

  const handleTogglePin = useCallback(async (conversation: ConversationSummary) => {
    const pinned = !conversation.pinned;
    setConversations((prev) =>
      prev.map((c) => (c.id === conversation.id ? { ...c, pinned } : c))
    );
    await updateConversation(conversation.id, { pinned });
  }, []);

  const handleToggleArchive = useCallback(
    async (conversation: ConversationSummary) => {
      const archived = !conversation.archived;
      setConversations((prev) => prev.filter((c) => c.id !== conversation.id));
      await updateConversation(conversation.id, { archived });
      if (conversation.id === activeId) navigate('/ai');
    },
    [activeId, navigate]
  );

  const handleDelete = useCallback(
    async (conversation: ConversationSummary) => {
      if (!window.confirm(`Delete "${conversation.title}"? This cannot be undone.`)) return;
      setConversations((prev) => prev.filter((c) => c.id !== conversation.id));
      const res = await deleteConversation(conversation.id);
      if (res.ok) toast.success('Conversation deleted');
      if (conversation.id === activeId) navigate('/ai');
    },
    [activeId, navigate]
  );

  const handleAnswer = useCallback(async (taskId: string, answer: string) => {
    await answerAgentQuestion(taskId, answer);
    setClarifyTaskId(null);
  }, []);

  const openRailTab = useCallback(
    (tab: RailTab) => {
      setRailTab(tab);
      setRailOpen(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('rail', tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Surface the first subtask that is blocked waiting on the operator.
  useEffect(() => {
    const blocked = agentTasks.find((t) => t.pendingQuestion && !t.pendingAnswer);
    setClarifyTaskId(blocked?.id ?? null);
  }, [agentTasks]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-2 lg:gap-3 lg:p-3">
      <div className="flex flex-wrap items-center gap-2 lg:gap-3">
        <button
          type="button"
          onClick={() => setSidebarOpen((v) => !v)}
          className="rounded-lg border border-zt-border p-1.5 text-zt-muted transition hover:border-zt-primary hover:text-zt-primary"
          aria-label={sidebarOpen ? 'Hide conversation list' : 'Show conversation list'}
          title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
        >
          {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>

        <StatusBar status={status} sandbox={sandbox} />

        <div className="flex flex-wrap items-center gap-1.5">
          <HubJump
            icon={ShieldCheck}
            label="Approvals"
            onClick={() => openRailTab('approvals')}
          />
          <HubJump icon={Inbox} label="Inbox" onClick={() => openRailTab('inbox')} />
          <HubJump icon={Brain} label="Memory" onClick={() => openRailTab('memory')} />
          <HubJump to="/approvals" icon={ExternalLink} label="Full queue" />
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2 lg:gap-3">
          <button
            type="button"
            onClick={() => setIntakeOpen((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition ${
              intakeOpen
                ? 'border-zt-primary text-zt-primary'
                : 'border-zt-border text-slate-200 hover:border-zt-primary hover:text-zt-primary'
            }`}
          >
            {intakeOpen ? <X size={14} /> : <FileUp size={14} />} Bulk intake
          </button>
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <span className="text-xs text-zt-muted">Template</span>
            <select
              value={template}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="rounded-lg border border-zt-border bg-zt-surface px-2 py-1.5 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
            >
              {TEMPLATES.map((t) => (
                <option key={t.value || 'default'} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <AgentModeToggle agentMode={agentMode} onChange={handleAgentModeChange} />
          <button
            type="button"
            onClick={() => setRailOpen((v) => !v)}
            className="rounded-lg border border-zt-border p-1.5 text-zt-muted transition hover:border-zt-primary hover:text-zt-primary"
            aria-label={railOpen ? 'Hide context rail' : 'Show context rail'}
            title={railOpen ? 'Hide context' : 'Show context'}
          >
            {railOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </button>
        </div>
      </div>

      {status && !status.configured && (
        <div className="flex items-center gap-2 rounded-lg border border-zt-danger/30 bg-zt-danger/5 px-3 py-2 text-xs text-zt-danger">
          <CircleAlert size={14} />
          No AI provider is configured. Set AI_API_KEY (or a fallback provider) to enable chat.
        </div>
      )}

      {intakeOpen && (
        <MultiFileDropzone
          conversationId={activeId}
          onComplete={() => setIntakeOpen(false)}
        />
      )}

      <div className="relative flex min-h-0 flex-1 gap-2 lg:gap-3">
        {isMobile && sidebarOpen && (
          <button
            type="button"
            className="absolute inset-0 z-10 bg-black/40"
            aria-label="Close conversations"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {sidebarOpen && (
          <div
            className={[
              'z-20 shrink-0',
              isMobile
                ? 'absolute inset-y-0 left-0 w-[min(18rem,85vw)] shadow-xl'
                : 'w-64',
            ].join(' ')}
          >
            <ConversationSidebar
              conversations={conversations}
              activeId={activeId}
              loading={listLoading}
              query={query}
              showArchived={showArchived}
              onQueryChange={setQuery}
              onToggleArchived={() => setShowArchived((v) => !v)}
              onSelect={(id) => {
                handleSelect(id);
              }}
              onNew={handleNew}
              onRename={(id, title) => void handleRename(id, title)}
              onTogglePin={(c) => void handleTogglePin(c)}
              onToggleArchive={(c) => void handleToggleArchive(c)}
              onDelete={(c) => void handleDelete(c)}
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <ChatPanel
            conversationId={activeId}
            template={template || undefined}
            agentMode={agentMode}
            onConversationId={handleConversationId}
            onConversationChanged={() => void loadConversations()}
            onAgentTasks={setAgentTasks}
            clarificationTaskId={clarifyTaskId}
            onAnswerClarification={(tid, ans) => void handleAnswer(tid, ans)}
          />
        </div>

        {isNarrow && railOpen && (
          <button
            type="button"
            className="absolute inset-0 z-10 bg-black/40"
            aria-label="Close context rail"
            onClick={() => setRailOpen(false)}
          />
        )}
        {railOpen && (
          <div
            className={[
              'z-20 shrink-0',
              isNarrow
                ? 'absolute inset-y-0 right-0 w-[min(20rem,90vw)] shadow-xl'
                : 'w-80',
            ].join(' ')}
          >
            <ContextRail
              tasks={agentTasks}
              activeTaskId={agentTasks[0]?.id}
              tab={railTab}
              onTabChange={openRailTab}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AiConsole;
